import { useState, useCallback } from 'react';

/**
 * Custom hook for copying text to clipboard with fallback support.
 * Handles both modern Clipboard API and legacy execCommand fallback.
 *
 * @param {number} resetDelay - Time in ms before copied state resets (default: 2000)
 * @returns {{ copied: boolean, copyToClipboard: (text: string) => Promise<boolean> }}
 */
function useCopyToClipboard(resetDelay = 2000) {
  const [copied, setCopied] = useState(false);

  const copyToClipboard = useCallback(async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), resetDelay);
      return true;
    } catch (err) {
      // Fallback for older browsers or when clipboard API fails
      try {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        // Avoid scrolling to bottom
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setCopied(true);
        setTimeout(() => setCopied(false), resetDelay);
        return true;
      } catch (fallbackErr) {
        return false;
      }
    }
  }, [resetDelay]);

  return { copied, copyToClipboard };
}

export default useCopyToClipboard;
